"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";
import { useSearchParams } from "next/navigation";
import { HelpCircle, Home, SearchIcon, ShoppingCart } from "lucide-react";
import {
  CommandDialog,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/shadcn/ui/command";
import { DialogDescription } from "@/shadcn/ui/dialog";

import searchIcon from "@/public/shop/search.svg";
import searchIconBlack from "@/public/shop/search-black.svg";

export default function Search({
  type,
  className,
}: {
  type: "light" | "transparent";
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const searchParams = useSearchParams();

  const formRef = React.useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    setSearch(searchParams?.get("q") || "");
  }, [searchParams]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (formRef.current) {
        formRef.current.requestSubmit();
      }
    }
  };

  return (
    <>
      <button
        className={clsx(
          "group h-full p-[2rem] hover:bg-white transition-colors duration-300",
          className,
        )}
        onClick={() => setOpen(true)}
      >
        <Image
          src={type === "transparent" ? searchIcon : searchIconBlack}
          alt="Suche"
          className={
            type === "transparent"
              ? "w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem] group-hover:invert transition-all"
              : "w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem]"
          }
        />
      </button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        className="right-[2rem] lg:max-w-[44rem] min-h-[50rem] left-1/2 lg:left-[auto] top-[2rem] -translate-x-1/2 lg:translate-x-0 translate-y-0 rounded-none p-[1.5rem] [&_.close-btn]:top-5 [&_.close-btn]:right-5 [&_.close-btn]:lg:top-6 [&_.close-btn]:lg:right-6"
      >
        <DialogDescription className="hidden"></DialogDescription>
        <h2 className="font-funnel text-[3rem]">Suche</h2>
        <form
          ref={formRef}
          action="/shop/search"
          className="w-full !mb-2 pb-[1rem]"
        >
          <div className="flex gap-[1rem] bg-white rounded-[.5rem] p-[.5rem] border border-black/10">
            <input
              key={searchParams?.get("q")}
              type="text"
              name="q"
              placeholder="Produkte suchen…"
              autoComplete="off"
              value={search}
              className="flex-1 px-4 py-2 text-black placeholder:text-neutral-500 outline-none"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />

            <button
              type="submit"
              onClick={() => setOpen(false)}
              className="w-fit flex h-fit items-center gap-[0.5rem] rounded-sm py-[.75rem] border border-[#1E3739]/50 px-[2rem] bg-[#1E3739] hover:bg-[#1E3739]/90 text-white transition-colors"
              disabled={!search}
            >
              <SearchIcon className="h-8 w-8" /> Suchen
            </button>
          </div>
        </form>
        <CommandList>
          <CommandGroup>
            <CommandItem>
              <Link
                href="/shop"
                className="w-full flex items-center gap-[0.5rem]"
                onClick={() => setOpen(false)}
              >
                <Home className="!h-7 !min-h-7 !w-7 !min-w-7" />
                Home
              </Link>
            </CommandItem>
            <CommandItem>
              <Link
                href="/shop/all"
                className="w-full flex items-center gap-[0.5rem]"
                onClick={() => setOpen(false)}
              >
                <ShoppingCart className="!h-7 !min-h-7 !w-7 !min-w-7" />
                Shop
              </Link>
            </CommandItem>
            <CommandItem>
              <Link
                href="/shop/support"
                className="w-full flex items-center gap-[0.5rem]"
                onClick={() => setOpen(false)}
              >
                <HelpCircle className="!h-7 !min-h-7 !w-7 !min-w-7" />
                Support
              </Link>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
